import { useState } from "react";

type ReportType = "comprehensive" | "trend" | "anomaly" | "forecast";

interface ReportConfig {
  type: ReportType;
  label: string;
  icon: string;
  desc: string;
  color: string;
}

const reportTypes: ReportConfig[] = [
  {
    type: "comprehensive",
    label: "综合分析报告",
    icon: "ri-file-chart-line",
    desc: "全维度购气行为分析，含用气规律、费用趋势、同比环比",
    color: "text-[#E8531A]",
  },
  {
    type: "trend",
    label: "趋势预测报告",
    icon: "ri-line-chart-line",
    desc: "基于历史数据AI预测未来3-6个月用气趋势及费用预算",
    color: "text-emerald-600",
  },
  {
    type: "anomaly",
    label: "异常分析报告",
    icon: "ri-alarm-warning-line",
    desc: "识别异常用气时段、突增突降事件及潜在安全风险",
    color: "text-amber-600",
  },
  {
    type: "forecast",
    label: "费用预测报告",
    icon: "ri-money-cny-circle-line",
    desc: "结合阶梯气价、季节因素生成个性化费用预测方案",
    color: "text-violet-600",
  },
];

const aiReportContent = `【AI智慧燃气 · 综合分析报告】
生成时间：2026-04-10 14:32:18
分析对象：老北京涮肉馆（C001）
分析周期：近一年（2025.01 - 2025.12）

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
一、用气总量分析
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
年度总用气量：15,081.6 m³
年度总购气金额：¥60,499.6
月均用气量：1,256.8 m³
月均购气金额：¥5,041.6

与上年同期相比：
• 总用气量增长 9.6%（上年：13,760.2 m³）
• 总购气金额增长 12.3%（含气价调整因素）

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
二、用气规律分析
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【季节性特征】
冬季（12-2月）用气量最高，月均约1,473 m³，
夏季（6-8月）用气量最低，月均约903 m³，
冬夏峰谷比约为1.63:1，季节性波动明显。

【高峰时段】
工作日：11:00-14:00（午餐高峰）、17:00-21:00（晚餐高峰）
周末：用气量较工作日平均高出约18%

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
三、AI智能建议
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✅ 建议在冬季来临前（10月）提前充值，避免余额不足影响营业
✅ 建议关注流量计校验日期（2026-05-20），提前预约校验
⚠️ 2025年11月检测到一次用气量异常波动，建议核查设备状态
✅ 当前安全评级A级，建议保持定期安检频率

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
四、费用预测（2026年Q2）
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
预计4月用气量：1,050 - 1,150 m³
预计4月费用：¥4,200 - ¥4,600
预计5月用气量：980 - 1,080 m³
预计5月费用：¥3,920 - ¥4,320

报告由 AI智慧燃气分析引擎 自动生成`;

export default function AIReportPanel() {
  const [selectedType, setSelectedType] = useState<ReportType>("comprehensive");
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleGenerate = () => {
    setGenerating(true);
    setGenerated(false);
    setTimeout(() => {
      setGenerating(false);
      setGenerated(true);
    }, 2200);
  };

  const handleDownload = () => {
    const blob = new Blob([aiReportContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `AI分析报告_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 flex items-center justify-center">
          <i className="ri-robot-2-line text-[#E8531A] text-xl"></i>
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">AI智能分析报告</h3>
          <p className="text-xs text-gray-400">基于历史购气数据，AI自动生成多维度分析报告</p>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {reportTypes.map(r => (
          <button
            key={r.type}
            onClick={() => setSelectedType(r.type)}
            className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
              selectedType === r.type
                ? "border-[#E8531A] bg-orange-50"
                : "border-gray-100 hover:border-gray-200"
            }`}
          >
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <i className={`${r.icon} text-lg ${selectedType === r.type ? r.color : "text-gray-400"}`}></i>
            </div>
            <div>
              <p className={`text-xs font-semibold ${selectedType === r.type ? "text-gray-900" : "text-gray-600"}`}>
                {r.label}
              </p>
              <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{r.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={generating}
        className="w-full py-2.5 bg-[#E8531A] text-white text-sm font-semibold rounded-lg hover:bg-[#d44a15] disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
      >
        {generating ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            AI正在分析数据...
          </>
        ) : (
          <>
            <i className="ri-magic-line"></i>
            生成AI分析报告
          </>
        )}
      </button>

      {/* Generated Result */}
      {generated && (
        <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-green-600 text-lg"></i>
              </div>
              <span className="text-sm font-semibold text-green-700">报告生成完成</span>
            </div>
            <span className="text-xs text-gray-400">2026-04-10 14:32:18</span>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            已完成对近一年购气数据的多维度分析，包含用气规律、趋势预测、费用分析及AI建议。
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex-1 py-2 border border-green-300 text-green-700 text-xs rounded-lg hover:bg-green-100 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
            >
              <i className="ri-eye-line"></i>
              {showPreview ? "收起预览" : "预览报告"}
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
            >
              <i className="ri-download-line"></i>
              下载报告
            </button>
          </div>

          {showPreview && (
            <div className="mt-3 p-3 bg-white rounded-lg border border-green-100 max-h-64 overflow-y-auto">
              <pre className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed font-mono">
                {aiReportContent}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
